import { EntityFragment } from "@/__generated__/graphql";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { useScope } from "@/hooks/useScope";
import { CommandCenterListFooter } from "@/layouts/AppLayout/CommandCenter/CommandCenterListFooter";
import { Objects } from "@/objects";
import { State } from "@/state";
import { useLayoutEffect, useRef } from "react";

export const COMMAND_ID = "select-entity";

export type SelectEntityContextType = {
  onSubmit?: (entity: EntityFragment) => void;
  entityIdentifiers?: string[];
  filterOptions?: (
    options: EntityFragment[],
    filter: string,
  ) => EntityFragment[];
};

function Component({
  commandInstanceId,
  commandDefinitionId,
  context,
}: {
  commandInstanceId: string;
  commandDefinitionId: string;
  context?: SelectEntityContextType;
}) {
  const activeIndexRef = useRef<{
    data: EntityFragment | null;
    onClick: () => void;
  }>({ data: null, onClick: () => {} });
  const { activeEntityIdentifiers } = useScope();
  const entityIdentifiers =
    context?.entityIdentifiers ?? activeEntityIdentifiers;

  return (
    <>
      <StickyLayout
        fillHeight={false}
        stickyBottomChildren={
          <CommandCenterListFooter
            buttons={[
              {
                variant: "default",
                size: "sm",
                children: "Submit",

                onClick: () => {
                  if (activeIndexRef.current.onClick) {
                    activeIndexRef.current.onClick();
                  }
                },

                hotkey: "enter",
              },
            ]}
          />
        }
      >
        <NodeList
          {...Objects.EntityObject.getReferenceValueOptions()}
          fillHeight={false}
          onSelectOption={(entity: EntityFragment) => {
            if (!context?.filterOptions) {
              State.setState({
                scopedEntityIdentifier: entity.identifier,
              });
            }
            context?.onSubmit?.(entity);
          }}
          filterOptions={
            context?.filterOptions ??
            ((options: EntityFragment[], filter: string) => {
              return options.filter((option) =>
                entityIdentifiers.includes(option.identifier),
              );
            })
          }
          onActiveIndexChange={(data: EntityFragment, onClick) => {
            activeIndexRef.current.data = data;
            activeIndexRef.current.onClick = onClick;
          }}
          className="max-w-none"
        />
      </StickyLayout>
    </>
  );
}

export function SelectEntityCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Select Entity",
      description: "Select an entity to switch context...",
      getLabel: () => {
        return "Select Entity";
      },
      component: Component,
      isHidden: true,
    });

    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, []);

  return null;
}
